import z from 'zod';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

const MINIMUM_ALLOWED_NUMBER = 0.01;

const minPriceMessage = i18n.t('general.notifications.priceMin', {min: MINIMUM_ALLOWED_NUMBER});

const scalarAmountSchema = z
  .union([z.string(), z.number()])
  .nullable()
  .optional()
  .refine((value) => (isNotNilOrEmpty(value) ? Number(value) >= MINIMUM_ALLOWED_NUMBER : true), {
    message: minPriceMessage,
  });

const vehiclePriceSchema = z.object({
  vatCode: z.string().min(1),
  withVat: z.object({
    amount: scalarAmountSchema,
  }),
  withoutVat: z.object({
    amount: scalarAmountSchema,
  }),
});

const foreignCurrencyPriceSchema = z
  .object({
    currency: z.string().optional().nullable(),
    withVat: scalarAmountSchema,
    withoutVat: scalarAmountSchema,
  })
  .superRefine((val, ctx) => {
    const hasAnyAmount = isNotNilOrEmpty(val.withVat) || isNotNilOrEmpty(val.withoutVat);
    const hasCurrency = isNotNilOrEmpty(val.currency);
    if (hasAnyAmount && !hasCurrency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['currency'],
        message: i18n.t`general.notifications.fieldIsRequired`,
      });
    }
  });

export const EditVehiclePriceFormSchema = z.object({
  isVatDeductible: z.boolean({
    required_error: i18n.t`general.notifications.fieldIsRequired`,
  }),
  purchasePrice: vehiclePriceSchema.nullable().optional(),
  sellingPrice: vehiclePriceSchema.nullable().optional(),
  purchasePriceInForeignCurrency: foreignCurrencyPriceSchema.nullable().optional(),
  sellingPriceInForeignCurrency: foreignCurrencyPriceSchema.nullable().optional(),
  originalCarPrice: z
    .union([z.string(), z.number()])
    .nullable()
    .optional()
    .transform((val) => (val ? String(val) : val)),
  updateReasonNote: z.string().nullable().optional(),
  b2bSellingPrice: vehiclePriceSchema.nullable().optional(),
});

export type EditVehiclePriceFormSchemaType = z.infer<typeof EditVehiclePriceFormSchema>;
